import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import RedactDialogFields from "../../field/redact/RedactDialogFields";
import {InputSwitch} from "primereact/inputswitch";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Blacken,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxRedactRedact
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class RedactDialogDefinition extends PortalDialog<ToolboxRedactRedact,
	PortalDialogProperties<ToolboxRedactRedact>,
	PortalDialogState<ToolboxRedactRedact>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<RedactDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.redact, Functions.getRedactDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.REDACT, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxRedactRedact | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.COLLISION)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-collision">
							<Translate {...this.props} data="redact.apply.properties.collision.label"
									   namespace="dialog"/>
							<Info {...this.props} data="redact.apply.properties.collision.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 slider-input">
							<InputNumber inputId="control-collision" step={1} suffix=" %" showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.blacken?.collisionRatio, Blacken.getCollisionRatioDefault()
										 )}
										 min={Blacken.getCollisionRatioMin()} max={Blacken.getCollisionRatioMax()}
										 onValueChange={(e) => this.onChange(
											 RedactDialogFields.COLLISION, e.value
										 )}/>
							<Slider step={1}
									value={FunctionHelper.applyValue(
										parameter?.blacken?.collisionRatio, Blacken.getCollisionRatioDefault()
									)}
									min={Blacken.getCollisionRatioMin()} max={Blacken.getCollisionRatioMax()}
									onChange={(e) => this.onChange(
										RedactDialogFields.COLLISION, e.value as number
									)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.ANNOTATIONS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-annotations"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-annotations"
										 checked={FunctionHelper.applyValue(
											 parameter?.blacken?.redactAnnotations,
											 Blacken.getRedactAnnotationsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 RedactDialogFields.ANNOTATIONS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-annotations">
								<Translate {...this.props} data="redact.apply.properties.annotations.label"
										   namespace="dialog"/>
								<Info {...this.props} data="redact.apply.properties.annotations.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.IMAGES)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-images"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-images"
										 checked={FunctionHelper.applyValue(
											 parameter?.blacken?.redactImages, Blacken.getRedactImagesDefault()
										 )}
										 onChange={(e) => this.onChange(
											 RedactDialogFields.IMAGES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-images">
								<Translate {...this.props} data="redact.apply.properties.images.label"
										   namespace="dialog"/>
								<Info {...this.props} data="redact.apply.properties.images.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.PATHES)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pathes"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-pathes"
										 checked={FunctionHelper.applyValue(
											 parameter?.blacken?.redactPaths, Blacken.getRedactPathsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 RedactDialogFields.PATHES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-pathes">
								<Translate {...this.props} data="redact.apply.properties.pathes.label"
										   namespace="dialog"/>
								<Info {...this.props} data="redact.apply.properties.pathes.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.TEXTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-texts"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-texts"
										 checked={FunctionHelper.applyValue(
											 parameter?.blacken?.redactTexts, Blacken.getRedactTextsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 RedactDialogFields.TEXTS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-texts">
								<Translate {...this.props} data="redact.apply.properties.texts.label"
										   namespace="dialog"/>
								<Info {...this.props} data="redact.apply.properties.texts.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["redact-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.redact.apply.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: RedactDialogFields, value: any): void {
		let parameter: ToolboxRedactRedact = this.state.parameter!.data;

		switch (field) {
			case RedactDialogFields.COLLISION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Blacken.getCollisionRatioDefault();
				}

				parameter.blacken!.collisionRatio = value;
				break;
			case RedactDialogFields.ANNOTATIONS:
				parameter.blacken!.redactAnnotations = value;
				break;
			case RedactDialogFields.IMAGES:
				parameter.blacken!.redactImages = value;
				break;
			case RedactDialogFields.PATHES:
				parameter.blacken!.redactPaths = value;
				break;
			case RedactDialogFields.TEXTS:
				parameter.blacken!.redactTexts = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-redact-apply small"}/>
					<Translate {...this.props} data="redact.apply.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			redact: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.redact.apply", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.REDACT;
	}

	protected getDefaultParameter(): Parameter<ToolboxRedactRedact> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const RedactDialog = React.memo(RedactDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxRedactRedact>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxRedactRedact>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RedactDialog;